"use client";

import { HopperThemingProvider } from "@hopper-b2b/hopper-theming";
import { getParentState } from "@hopper-b2b/hopper-utils";
import { AuthModal } from "@hopper-b2b/ui-core";
import { isHopperAppWebView } from "@hopper-b2b/utilities";
import { useInterpret } from "@xstate/react";
import { AuthMachine, authMachine, useAuthState } from "../../machine";
import { getIsLoading } from "../../machine/selectors";
import { AuthFlowProps } from "./AuthFlow";
import { AuthStateContent } from "./AuthStateContent";
import { useWebviewAuth } from "./useWebviewAuth";
import "./styles.scss";

export type AuthPromptProps = AuthFlowProps & {
  open: boolean;
};

const WebviewAuthPrompt = (props: AuthPromptProps) => {
  useWebviewAuth(props.open);
  return null;
};

const WebAuthPrompt = (props: AuthPromptProps) => {
  // useInterpret restarts the service if React remounts it (Strict Mode in dev), unlike a service started in
  // useState and stopped on cleanup, which stays stopped and drops every event.
  const service = useInterpret(authMachine, {
    devTools: import.meta.env.DEV,
  });

  return (
    <AuthMachine.Provider value={service as any}>
      <AuthModalWrapper {...props} />
    </AuthMachine.Provider>
  );
};

export const AuthPrompt = (props: AuthPromptProps) => {
  if (isHopperAppWebView()) {
    return <WebviewAuthPrompt {...props} />;
  }
  return <WebAuthPrompt {...props} />;
};

const AuthModalWrapper = ({ open, onClose, ...rest }: AuthPromptProps) => {
  const [state] = useAuthState();
  const parentState = getParentState(state.value);
  const loading = getIsLoading(state.value);
  const disableOnClose = loading || parentState === "success";

  return (
    <HopperThemingProvider enableHDSTheme>
      <AuthModal open={open} onClose={disableOnClose ? undefined : onClose}>
        <AuthStateContent onClose={onClose} {...rest} />
      </AuthModal>
    </HopperThemingProvider>
  );
};
